/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */

import { Fragment, useState, useCallback } from 'react';

import { jsx } from '@compiled/react';
import {
	defineMessages,
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
} from 'react-intl';

import { css, cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

export const messages = defineMessages({
	modified: {
		id: 'smart-knowledge-cards.link-embed.modified',
		defaultMessage: 'Modified ',
		description: 'Label before the relative time',
	},
	modifiedBy: {
		id: 'smart-knowledge-cards.link-embed.modified-by',
		defaultMessage: ' by {author}',
		description: 'Text showing who last modified the document',
	},
	open: {
		id: 'smart-knowledge-cards.link-embed.open',
		defaultMessage: 'Open',
		description: 'Button label to open the link in a new tab',
	},
	copy: {
		id: 'smart-knowledge-cards.link-embed.copy',
		defaultMessage: 'Copy',
		description: 'Button label to copy the link to clipboard',
	},
});

export interface LinkEmbedVariantProps {
	iconUrl: string;
	isError: boolean;
	isOlderThanWeek: boolean | null;
	modifiedBy: string;
	previewHref: string;
	provider: string;
	secondsAgo: number;
	title: string;
	updatedDate: Date | null;
	url: string;
}

const iconTileStyles = css({
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.large'),
	width: '32px',
	height: '32px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
});

const titleContainerStyles = css({
	flex: 1,
	minWidth: '0px',
	overflow: 'hidden',
});

const embedContainerStyles = css({
	width: '100%',
	marginTop: token('space.100'),
	position: 'relative',
});

const overlayStyles = cssMap({
	root: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.sunken'),
	},
});

export const EmbedPreview = ({
	url,
	previewHref,
	inheritDimensions = false,
}: {
	inheritDimensions?: boolean;
	previewHref: string;
	url: string;
}) => {
	const [resolved, setResolved] = useState(false);
	const handleResolve = useCallback(() => setResolved(true), []);
	const handleError = useCallback(() => setResolved(true), []);

	if (!previewHref) {
		return null;
	}

	const card = (
		<>
			<Card
				appearance="embed"
				url={url}
				frameStyle="hide"
				inheritDimensions={inheritDimensions}
				onResolve={handleResolve}
				onError={handleError}
			/>
			{!resolved && <Box xcss={overlayStyles.root} />}
		</>
	);

	return inheritDimensions ? card : <div css={embedContainerStyles}>{card}</div>;
};

export const IconTile = ({ iconUrl, isError }: { iconUrl: string; isError: boolean }) => (
	<div css={iconTileStyles}>
		{iconUrl ? (
			<img src={iconUrl} alt="" width={16} height={16} />
		) : isError ? (
			<LinkIcon label="" />
		) : null}
	</div>
);

export const TitleMeta = ({
	title,
	provider,
	updatedDate,
	secondsAgo,
	isOlderThanWeek,
	modifiedBy,
}: Pick<
	LinkEmbedVariantProps,
	'title' | 'provider' | 'updatedDate' | 'secondsAgo' | 'isOlderThanWeek' | 'modifiedBy'
>) => (
	<div css={titleContainerStyles}>
		<Stack space="space.0">
			<Text size="medium" weight="semibold" maxLines={1}>
				{title}
			</Text>
			<Text size="small" color="color.text.subtle" maxLines={1}>
				{provider}
				{updatedDate && (
					<>
						<span aria-hidden="true">
							<Text size="small" color="color.text.subtle">
								{' · '}
							</Text>
						</span>
						<FormattedMessage {...messages.modified} />
						{isOlderThanWeek ? (
							<FormattedDate value={updatedDate} month="short" day="numeric" year="numeric" />
						) : (
							<FormattedRelativeTime
								value={-secondsAgo}
								unit="second"
								updateIntervalInSeconds={60}
							/>
						)}
						{modifiedBy && (
							<FormattedMessage {...messages.modifiedBy} values={{ author: modifiedBy }} />
						)}
					</>
				)}
			</Text>
		</Stack>
	</div>
);
